import { useEffect } from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@ai-tutor/ui';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
}: ConfirmDialogProps) {
  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Tone the destructive action down - was bright primary red (red-600
  // bg, red-100 icon halo) which read as alarming. The new palette uses
  // a softer pink-tinted icon bg + a deeper, more confident wine-red
  // button. Still legible as destructive, just not screaming.
  // Light classes + solid action buttons unchanged; only the dark ICON tints are
  // tokenized to the semantic status tokens (buttons are solids, not tints).
  const variantStyles = {
    danger: {
      Icon: AlertTriangle,
      icon: 'bg-red-50 text-red-700 dark:bg-destructive/20 dark:text-destructive-emphasis',
      button:
        'bg-red-700 hover:bg-red-800 dark:bg-red-800 dark:hover:bg-red-900 text-white',
    },
    warning: {
      Icon: AlertTriangle,
      icon: 'bg-amber-50 text-amber-700 dark:bg-warning/20 dark:text-warning',
      button:
        'bg-amber-700 hover:bg-amber-800 dark:bg-amber-800 dark:hover:bg-amber-900 text-white',
    },
    // A reversible checkpoint (pausing reviews, switching version): the app's
    // primary accent, the same as the primary actions around it, so it reads
    // as a normal step rather than a caution.
    info: {
      Icon: Info,
      icon: 'bg-primary/10 text-primary',
      button: 'bg-primary text-primary-foreground hover:bg-primary/90',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/50 backdrop-blur-sm duration-200 animate-in fade-in'
        onClick={onClose}
      />

      {/* Modal */}
      <div className='relative w-full max-w-md rounded-2xl bg-card shadow-2xl duration-200 animate-in fade-in zoom-in-95'>
        {/* Close button */}
        <button
          onClick={onClose}
          // `-m-2 p-3` below `sm`: a 44px touch target whose negative margin
          // keeps the icon where the 28px desktop button has it.
          className='absolute right-4 top-4 -m-2 rounded-lg p-3 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground sm:m-0 sm:p-1'
          aria-label='Close dialog'
        >
          <X className='h-5 w-5' />
        </button>

        {/* Content */}
        <div className='p-6'>
          {/* Header row - icon + title, side by side */}
          <div className='mb-3 flex items-center gap-3'>
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${styles.icon}`}
            >
              <styles.Icon className='h-5 w-5' aria-hidden />
            </div>
            <h2 className='text-xl font-semibold text-foreground'>{title}</h2>
          </div>

          {/* Description */}
          <p className='mb-6 leading-relaxed text-muted-foreground'>
            {description}
          </p>

          {/* Actions */}
          <div className='flex gap-3 max-sm:[&>button]:h-11'>
            <Button
              onClick={onClose}
              variant='outline'
              className='flex-1 border-gray-300 text-foreground hover:bg-muted dark:border-gray-600'
            >
              {cancelText}
            </Button>
            <Button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`flex-1 ${styles.button}`}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
